/**
 * Edit a saved repeating schedule (2026-10-30): what it does, when it
 * runs, its name and its end, with a live line that says what saving
 * will do BEFORE it is saved.
 *
 * The "+ New task" drawer's shell (`NewTaskDrawer`): the same size, the
 * same Escape / ✕ / press-outside, focus to the first field on opening.
 * Like that drawer, closing HIDES it -- the screen keeps a half-made
 * edit mounted until another schedule is edited or this one is saved --
 * so a stray click on the calendar does not cost the edit. Only a
 * half-made edit is kept: the form says when it holds one
 * (`onDirtyChange`), and an Edit pressed on a form that holds none
 * opens a fresh one on the schedule as it is NOW.
 *
 * TWO KINDS OF CHANGE, and the api-server (0.6.54) treats them
 * differently, so the drawer says which one an edit is:
 *
 *  - DETAILS (name, zone or place, clean mode, priority, an end date):
 *    applied in place, to every run from now.
 *  - TIMING (the start, the pattern, "after N runs"): applied FROM A
 *    DATE ON. The past is never rewritten -- runs before the cut keep
 *    their recorded outcomes -- and the schedule keeps its id. "Applies
 *    from" picks the day (default today); the server lifts it to now and
 *    past the last recorded run, and never runs a schedule twice in one
 *    day because of an edit. Old runs still to come before the first
 *    new day keep their old time on a second schedule of the same name
 *    (the carry-over), which the summary names before Save.
 *
 * NOTHING IS RE-DERIVED THAT THE OPERATOR DID NOT CHANGE. A stored rule
 * comes into the editor only through the strict inverse (`specFromRule`)
 * and the stored start through `dtstartFromInput`, which hands back the
 * stored instant -- seconds and all -- while its field is untouched; the
 * task through `formFromTaskRequest`, and back as a diff over the stored
 * task (`taskRequestFromForm`), so a robot pin, phase ids and params the
 * form never writes survive. The PATCH carries only what differs
 * (`buildSchedulePatch`), and a rule this editor cannot draw leaves the
 * When section read-only rather than being flattened into one it can.
 *
 * Every wall clock here is the SCHEDULE's zone (`schedule.timezone`),
 * never the site's: the create form writes the site's, and an editor
 * that did the same would quietly re-time a schedule stored in another
 * zone -- the case the Month sidebar warns about.
 *
 * The summary is the server's dry run of the same body
 * (`useScheduleUpdatePreview`), asked once the form has been still for
 * 400 ms, so the words under the form are the PATCH's own computation.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type {
  PlaceRegistry,
  ScheduleUpdatePreview,
  TaskCatalogEntry,
  TaskSchedule,
  UpdateTaskScheduleRequest,
} from '../../api/v1';
import { ApiClientError } from '../../api/v1-client';
import { useScheduleUpdatePreview, useUpdateSchedule } from '../../api/hooks/useSchedules';
import { useMaintenanceWindows, windowCovering } from '../../api/hooks/useMaintenance';
import { usePlaces } from '../../api/hooks/usePlaces';
import { useTaskCatalog } from '../../api/hooks/useTaskCatalog';
import { useActionsOff } from '../../auth/useRolePreview';
import { DateField, DateTimeField } from '../../design-system/components/DateField';
import { HelpNote } from '../../help';
import { NewTaskDrawer } from './NewTaskDrawer';
import { RecurrenceEditor } from './RecurrenceEditor';
import { WaypointMultiSelect } from './WaypointMultiSelect';
import { CATEGORY_LABEL, laneOfSchedule } from './calendar-categories';
import { dateKeyOf, type DateKey } from './calendar/lane-time';
import { useMinuteNow } from './calendar/useMinuteNow';
import { scheduleRobotText } from './calendar/phone-model';
import { PREVIEW_LINE } from './calendar/useScheduleActions';
import {
  CLEAN_MODE_LABEL,
  ROUTINE_CHECK_LABEL,
  offeredCleanModes,
  type CleanMode,
} from './clean-modes';
import {
  buildRRule,
  buildUntilMs,
  describeRule,
  dtstartFromInput,
  dtstartInputValue,
  dtstartWallDate,
  isUnsupportedRule,
  specFromRule,
  timingChanged,
  type RecurrenceSpec,
} from './rrule';
import {
  applyFromMsFor,
  editSummary,
  hasScheduleHistory,
  runText,
  type SummaryLine,
} from './schedule-edit-summary';
import { buildSchedulePatch, type ScheduleEdit } from './schedule-patch';
import {
  DUSTBAG_WAIT_MAX_MINUTES,
  DUSTBAG_WAIT_MIN_MINUTES,
  formFromTaskRequest,
  isUnsupportedTask,
  parseDustbagWaitMinutes,
  taskRequestFromForm,
  toV1Category,
  type CategoryFormValues,
  type TaskRequestForm,
} from './task-request-form';
import { locationFromRequest } from './task-location';
import { PriorityChoice } from './TaskPriority';
import { priorityEffects, useCanSetHighPriority } from './task-priority';
import {
  isHighPriority,
  isHighPriorityRefusal,
  priorityLevel,
  type PriorityLevel,
} from '../../api/task-priority';
import { timeZoneLabel, wallClockToEpochMs, zonedParts } from './zoned-time';
import './TaskDispatchForm.css';
import './ScheduleEditDrawer.css';

/** How long the form must be still before the dry run is asked. */
const PREVIEW_DEBOUNCE_MS = 400;

const NAME_MAX = 120;

export interface ScheduleEditDrawerProps {
  open: boolean;
  /** The schedule as the screen's list has it now; null once it has
   *  gone from the site. The form edits a SNAPSHOT taken when it
   *  mounted, so another person's skip or pause meanwhile does not move
   *  the fields under the operator, and the PATCH carries only what
   *  this operator changed (the last writer wins field by field). When
   *  the stored row changes under a half-made edit, the form says so
   *  and offers to start again from it (`onReload`). */
  schedule: TaskSchedule | null;
  /** The site's schedules as listed now: the summary names the other
   *  rows of this one's plan a retime cuts short by their time and end. */
  schedules?: readonly TaskSchedule[];
  siteTimeZone: string;
  /** "Applies from", preset: the run an editor was opened from. A day
   *  before today reads as today -- the past is never rewritten. A new
   *  preset on a mounted form moves the field, unless the operator has
   *  set it themselves. */
  applyFromMs?: number | null;
  onClose: () => void;
  /** Whether the form holds a change the operator made -- any field
   *  moved off what it opened with, valid or not. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Drop the edit and open the form again on the stored row. */
  onReload?: () => void;
  /** `keptUntilMs`: when the old runs this save kept on a second
   *  schedule end (the dry run's `kept_until_ms` for the body saved),
   *  null when it kept none. Save waits for that dry run, so it has
   *  always answered. */
  onSaved: (saved: TaskSchedule, keptUntilMs: number | null) => void;
  triggerRef: RefObject<HTMLElement | null>;
}

export function ScheduleEditDrawer(props: ScheduleEditDrawerProps) {
  // The snapshot (see `schedule`). Taken once; the screen remounts the
  // drawer (a new `key`) to edit another schedule.
  const [before] = useState(props.schedule);
  return (
    <NewTaskDrawer
      open={props.open}
      onClose={props.onClose}
      triggerRef={props.triggerRef}
      title="Edit schedule"
      id="nx-schedule-edit"
      testId="schedule-edit-drawer"
    >
      {before ? (
        <ScheduleEditForm
          before={before}
          live={props.schedule}
          listed={props.schedules}
          open={props.open}
          siteTimeZone={props.siteTimeZone}
          presetApplyFromMs={props.applyFromMs ?? null}
          onCancel={props.onClose}
          onSaved={props.onSaved}
          onDirtyChange={props.onDirtyChange}
          onReload={props.onReload}
        />
      ) : (
        <p className="nx-sched-edit__gone">This schedule is no longer on the site.</p>
      )}
    </NewTaskDrawer>
  );
}

/** `value`, once it has stopped changing for `ms`. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/** A `YYYY-MM-DD` on the schedule's calendar. */
function ymdOf(ms: number, timeZone: string): string {
  const { year, month, day } = zonedParts(ms, timeZone);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** The end of `ymd` on the schedule's clock -- the editor's end, as
 *  `buildUntilMs` writes it -- or null for a value that is not a date. */
function endOfDay(ymd: string, timeZone: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  return wallClockToEpochMs(
    {
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      hour: 23,
      minute: 59,
      second: 59,
    },
    timeZone,
  );
}

function sameSpec(a: RecurrenceSpec, b: RecurrenceSpec): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The rule checker's refusals that reach an operator, in their words
 *  (`scheduler.validate_rule`); the edit planner's own refusals are
 *  already written for them and pass through. */
const RULE_REFUSAL_WORDS: Record<string, string> = {
  'this rule ends before its first occurrence':
    'It would end before its first run. Pick a later last day.',
  'this rule never produces an occurrence':
    'This pattern never gives a run. Pick a different pattern.',
};

/** What the dry run or the save came back with, in words. */
function errorWords(err: unknown): string {
  // High from someone who may not set it: the api-server's own words,
  // which say what to do, not "your role cannot change schedules".
  if (isHighPriorityRefusal(err)) return err.message;
  if (err instanceof ApiClientError) {
    if (err.status === 503) {
      return 'The server could not check this change just now. Try again in a moment.';
    }
    if (err.status === 403) return 'Your role cannot change schedules.';
    if (err.status === 404) return 'This schedule is no longer on the site.';
    if (err.status === 422) return 'The server could not read this change.';
    return RULE_REFUSAL_WORDS[err.message] ?? err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

/** The type the schedule keeps, as the calendar names it. */
function typeText(form: TaskRequestForm | null, category: string): string {
  if (form?.category === 'clean' || category === 'clean') return 'Clean';
  if (category === 'dustbag') return ROUTINE_CHECK_LABEL;
  const lane = category as keyof typeof CATEGORY_LABEL;
  return CATEGORY_LABEL[lane] ?? category;
}

function ScheduleEditForm(props: {
  before: TaskSchedule;
  live: TaskSchedule | null;
  listed?: readonly TaskSchedule[];
  open: boolean;
  siteTimeZone: string;
  presetApplyFromMs: number | null;
  onCancel: () => void;
  onSaved: (saved: TaskSchedule, keptUntilMs: number | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onReload?: () => void;
}) {
  const { before, siteTimeZone } = props;
  const tz = before.timezone || siteTimeZone;
  const nowMs = useMinuteNow();
  const todayKey = dateKeyOf(nowMs, tz);
  const actionsOff = useActionsOff();
  const catalogQuery = useTaskCatalog();
  const placesQuery = usePlaces();
  const update = useUpdateSchedule();
  // The snapshot's past, decided once: a schedule whose start passes
  // while the drawer is open does not change shape under the operator.
  const [hasHistory] = useState(() => hasScheduleHistory(before, Date.now()));

  // ---- What ----------------------------------------------------------------
  const task0 = useMemo(() => formFromTaskRequest(before.task), [before.task]);
  const [taskForm, setTaskForm] = useState<TaskRequestForm | null>(() =>
    isUnsupportedTask(task0) ? null : task0,
  );
  const setValue = (key: keyof CategoryFormValues, value: string) =>
    setTaskForm((f) => (f ? { ...f, values: { ...f.values, [key]: value } } : f));
  // Its own setter, a NUMBER: through `setValue` the level would be stored
  // as "1", which `priorityLevel` reads as Normal and which never equals
  // the stored 1, so every open would count as an edit.
  const setPriority = (level: PriorityLevel) =>
    setTaskForm((f) => (f ? { ...f, values: { ...f.values, priority: level } } : f));
  const canSetHigh = useCanSetHighPriority();

  // ---- When ----------------------------------------------------------------
  const spec0 = useMemo(
    () => specFromRule(before.rrule, before.dtstart_ms, before.until_ms, tz),
    [before.rrule, before.dtstart_ms, before.until_ms, tz],
  );
  const [spec, setSpec] = useState<RecurrenceSpec | null>(() =>
    isUnsupportedRule(spec0) ? null : spec0,
  );
  const [start0] = useState(() => dtstartInputValue(before.dtstart_ms, tz));
  const [startInput, setStartInput] = useState(start0);
  // A rule the editor cannot draw can still be ENDED on a day -- unless
  // its end is part of the rule (COUNT, UNTIL), which only the rule can
  // change. Untouched, the stored end goes back exactly as it was, even
  // one at a moment the date field cannot hold.
  const endInRule = /(^|;)\s*(COUNT|UNTIL)\s*=/i.test(before.rrule);
  const [endTouched, setEndTouched] = useState(false);
  const [endOn, setEndOn] = useState(before.until_ms !== null && before.until_ms !== undefined);
  const [endDate, setEndDate] = useState(() =>
    before.until_ms !== null && before.until_ms !== undefined ? ymdOf(before.until_ms, tz) : '',
  );

  const [name, setName] = useState(before.name);
  const presetDay = (ms: number | null): DateKey => {
    const preset = ms !== null ? dateKeyOf(ms, tz) : todayKey;
    return preset < todayKey ? todayKey : preset;
  };
  const [applyFromKey, setApplyFromKey] = useState<DateKey>(() =>
    presetDay(props.presetApplyFromMs),
  );
  const [applyFromTouched, setApplyFromTouched] = useState(false);
  // Opened again, from another run, on this same half-made edit: the
  // new run's day -- unless the operator picked the day themselves.
  const [presetSeen, setPresetSeen] = useState(props.presetApplyFromMs);
  if (presetSeen !== props.presetApplyFromMs) {
    setPresetSeen(props.presetApplyFromMs);
    if (!applyFromTouched) setApplyFromKey(presetDay(props.presetApplyFromMs));
  }

  // ---- A change the operator made -------------------------------------------------
  // Any field off what it opened with, valid or not: a name cleared to
  // be retyped is a change worth keeping, though it cannot be saved.
  const dirty =
    name !== before.name ||
    JSON.stringify(taskForm) !== JSON.stringify(isUnsupportedTask(task0) ? null : task0) ||
    (spec !== null && !isUnsupportedRule(spec0) && !sameSpec(spec, spec0)) ||
    startInput !== start0 ||
    endTouched ||
    applyFromTouched;
  const onDirtyChange = props.onDirtyChange;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  // The stored row moved on since the snapshot: a timing save would send
  // the whole pattern this form holds, putting back what changed since.
  const changedUnder =
    dirty && props.live !== null && props.live.updated_at_ms !== before.updated_at_ms;

  // ---- The edit, as the server would get it ---------------------------------
  const problems: string[] = [];
  const dtstartMs = spec ? dtstartFromInput(startInput, before.dtstart_ms, tz) : before.dtstart_ms;
  if (dtstartMs === null) problems.push('The start is not a date and time.');
  let rrule = before.rrule;
  let untilMs: number | null = before.until_ms ?? null;
  if (spec && !isUnsupportedRule(spec0) && dtstartMs !== null) {
    const untouched = sameSpec(spec, spec0) && dtstartMs === before.dtstart_ms;
    rrule = untouched ? before.rrule : buildRRule(spec, dtstartWallDate(dtstartMs, tz));
    untilMs = sameSpec(spec, spec0) ? (before.until_ms ?? null) : buildUntilMs(spec, tz);
    if (spec.end === 'on' && !spec.untilDate) problems.push('Pick the last day it runs.');
  } else if (!spec && endTouched && !endInRule) {
    untilMs = endOn ? endOfDay(endDate, tz) : null;
    if (endOn && untilMs === null) problems.push('Pick the last day it runs.');
  }
  // Said here, before any dry run: the server's own words for it are
  // about rules and occurrences.
  if (untilMs !== null && dtstartMs !== null && untilMs < dtstartMs) {
    problems.push('It would end before it starts. Pick a later last day.');
  }
  const trimmed = name.trim();
  if (!trimmed) problems.push('Give the schedule a name.');
  if (trimmed.length > NAME_MAX) problems.push(`Keep the name under ${NAME_MAX} characters.`);
  const task = taskForm ? taskRequestFromForm(taskForm, before.task) : before.task;
  if (isUnsupportedTask(task)) problems.push(`It can't be saved: ${task.unsupported}.`);
  const waitBad =
    taskForm?.category === 'dustbag' &&
    parseDustbagWaitMinutes(taskForm.values.dustbagWaitMinutes) === 'invalid';

  const edit: ScheduleEdit | null =
    problems.length === 0 && !isUnsupportedTask(task) && dtstartMs !== null
      ? {
          name: trimmed,
          task,
          rrule,
          dtstart_ms: dtstartMs,
          timezone: before.timezone,
          until_ms: untilMs,
        }
      : null;
  const timing = edit !== null && timingChanged(before, edit);
  const showApplyFrom = timing && hasHistory;
  const applyFromMs = showApplyFrom ? applyFromMsFor(applyFromKey, todayKey, tz) : null;
  const patch = edit ? buildSchedulePatch(before, edit, applyFromMs) : null;

  // ---- The dry run ------------------------------------------------------------
  const patchKey = patch ? JSON.stringify(patch) : null;
  const settledKey = useSettled(patchKey, PREVIEW_DEBOUNCE_MS);
  const settledPatch = useMemo<UpdateTaskScheduleRequest | null>(
    () => (settledKey ? (JSON.parse(settledKey) as UpdateTaskScheduleRequest) : null),
    [settledKey],
  );
  const preview = useScheduleUpdatePreview(
    props.open ? before.schedule_id : null,
    props.open ? settledPatch : null,
  );
  // The answer is about the body in the form only when the form has
  // settled on it and the answer is not the previous body's, kept on
  // screen while this one loads.
  const answered = patchKey !== null && settledKey === patchKey && !preview.isPlaceholderData;
  const previewData: ScheduleUpdatePreview | undefined = answered ? preview.data : undefined;
  const previewError = answered && preview.isError ? preview.error : null;

  const spec0Count = !isUnsupportedRule(spec0) && spec0.end === 'after' ? spec0.count : null;
  const countChanged = spec !== null && spec.end === 'after' && spec.count !== spec0Count;
  const lines: SummaryLine[] = previewData
    ? editSummary({
        before,
        preview: previewData,
        timingAsked: timing,
        nowMs,
        timeZone: tz,
        siteTimeZone,
        hasHistory,
        applyFromKey: showApplyFrom ? applyFromKey : null,
        countChanged,
        listed: props.listed,
      })
    : [];

  // ---- Blocked time the next runs fall into ------------------------------------
  const runs = previewData?.next_runs_ms ?? [];
  const lastRun = runs.length ? runs[runs.length - 1]! : null;
  const maintenance = useMaintenanceWindows(
    props.open && lastRun !== null ? { fromMs: nowMs, toMs: lastRun + 1 } : null,
  );
  const blocked = runs
    .map((ms) => ({ ms, w: windowCovering(maintenance.data, ms) }))
    .filter((r) => r.w !== undefined);

  // ---- Save ----------------------------------------------------------------------
  // Save waits for the dry run of the body in the form: until it has
  // answered, the lines above Save -- what is kept, dropped, lost today
  // -- have not been shown, and the save would go out unseen (a click,
  // or Enter in a field, inside the debounce).
  const busy = update.isPending;
  const checking = patch !== null && previewData === undefined && !previewError;
  const canSave =
    patch !== null && previewData !== undefined && !previewError && !busy && !actionsOff;
  function save() {
    if (!canSave || !patch) return;
    // The dry run of this very body: what the save kept at the old
    // time, for the screen's "Schedule updated" line.
    const keptUntilMs = previewData?.kept_until_ms ?? null;
    update.mutate(
      { scheduleId: before.schedule_id, patch },
      { onSuccess: (saved) => props.onSaved(saved, keptUntilMs) },
    );
  }
  // A dry run moved on from a failed save: the error was about a body
  // that is no longer in the form.
  const resetUpdate = update.reset;
  const lastPatchKey = useRef(patchKey);
  useEffect(() => {
    if (lastPatchKey.current !== patchKey) {
      lastPatchKey.current = patchKey;
      resetUpdate();
    }
  }, [patchKey, resetUpdate]);

  const zoneDiffers = tz !== siteTimeZone;

  return (
    <form
      className="nx-task-form nx-sched-edit"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      data-testid="schedule-edit-form"
    >
      <HelpNote id="tasks.schedule_edit" />
      <fieldset className="nx-task-form__fieldset nx-sched-edit__fieldset" disabled={busy}>
        <section className="nx-sched-edit__section" aria-labelledby="nx-sched-edit-what">
          <h3 className="nx-sched-edit__h" id="nx-sched-edit-what">
            What
          </h3>
          <div className="nx-task-form__row">
            <span>Type</span>
            <span className="nx-sched-edit__value" data-testid="schedule-edit-type">
              {typeText(taskForm, before.category)}
            </span>
            <span className="nx-task-form__hint">
              A schedule keeps its type. For another type, make a new schedule.
            </span>
          </div>
          {taskForm ? (
            <TaskFields
              form={taskForm}
              original={task0 as TaskRequestForm}
              onMode={(mode) => setTaskForm((f) => (f ? { ...f, cleanMode: mode } : f))}
              onValue={setValue}
              catalog={catalogQuery.data ?? []}
              places={placesQuery.data ?? []}
              pinnedFleet={before.task.fleet?.trim() || null}
              waitBad={waitBad}
            />
          ) : (
            <div className="nx-task-form__row" data-testid="schedule-edit-what-readonly">
              <span>Where</span>
              <span className="nx-sched-edit__value">
                {locationFromRequest(before.category, before.task)?.text ?? '—'}
              </span>
              <span className="nx-task-form__hint">
                Its task can&apos;t be changed here because{' '}
                {(task0 as { unsupported: string }).unsupported}. You can still change the name, the
                time and the end.
              </span>
            </div>
          )}
          {/* A detail like the zone: every run from now. High stays
              choosable for anyone editing a schedule a supervisor made
              High -- keeping High is not making it (`task_priority.py`). */}
          {taskForm && (
            <PriorityChoice
              value={priorityLevel(taskForm.values.priority)}
              onChange={setPriority}
              canSetHigh={canSetHigh}
              storedHigh={isHighPriority(before.task.priority)}
              effects={priorityEffects(catalogQuery.data)}
              testId="schedule-edit-priority"
            />
          )}
          <div className="nx-task-form__row">
            <span>Robot</span>
            <span className="nx-sched-edit__value" data-testid="schedule-edit-robot">
              {scheduleRobotText(before, noRobotLabel, laneOfSchedule(before))}
            </span>
            {before.task.robot?.trim() && (
              <span className="nx-task-form__hint">
                Chosen when the schedule was made. Edit keeps it.
              </span>
            )}
          </div>
        </section>

        <section className="nx-sched-edit__section" aria-labelledby="nx-sched-edit-when">
          <h3 className="nx-sched-edit__h" id="nx-sched-edit-when">
            When
          </h3>
          {spec ? (
            <>
              <DateTimeField
                className="nx-task-form__row"
                label="Starts"
                ariaLabel="Start time"
                value={startInput}
                onChange={setStartInput}
                data-testid="schedule-edit-start"
              />
              {hasHistory && (
                <span className="nx-task-form__hint">
                  Its time of day, and the weekday or day of the month it repeats on, are read from
                  this start.
                </span>
              )}
              {zoneDiffers && (
                <span
                  className="nx-task-form__hint nx-task-form__hint--warn"
                  data-testid="schedule-edit-tz-note"
                >
                  This schedule runs on {timeZoneLabel(tz, before.dtstart_ms)}, not this site&apos;s{' '}
                  {timeZoneLabel(siteTimeZone, before.dtstart_ms)}. Times here are its own.
                </span>
              )}
              <RecurrenceEditor
                spec={spec}
                onChange={setSpec}
                dtstart={dtstartWallDate(dtstartMs ?? before.dtstart_ms, tz)}
                timeZone={tz}
              />
            </>
          ) : (
            <>
              <div className="nx-task-form__row" data-testid="schedule-edit-when-readonly">
                <span>Repeats</span>
                <span className="nx-sched-edit__value">
                  {describeRule(before.rrule, before.dtstart_ms, before.until_ms, tz, {
                    siteTimeZone,
                  })}
                </span>
                <span className="nx-task-form__hint">
                  This pattern was set up elsewhere; you can still change the details, name and end.
                </span>
                {isUnsupportedRule(spec0) && (
                  <span className="nx-task-form__hint nx-sched-edit__why">
                    Why: {spec0.unsupported}.
                  </span>
                )}
              </div>
              {endInRule ? (
                <div className="nx-task-form__row">
                  <span>Ends</span>
                  <span className="nx-sched-edit__value">As the pattern says</span>
                </div>
              ) : (
                <div className="nx-task-form__row">
                  <span>Ends</span>
                  <select
                    value={endOn ? 'on' : 'never'}
                    onChange={(e) => {
                      setEndTouched(true);
                      setEndOn(e.target.value === 'on');
                    }}
                    aria-label="Schedule end"
                    data-testid="schedule-edit-end"
                  >
                    <option value="never">Never</option>
                    <option value="on">On date</option>
                  </select>
                  {endOn && (
                    <DateField
                      value={endDate}
                      min={ymdOf(before.dtstart_ms, tz)}
                      onChange={(v) => {
                        setEndTouched(true);
                        setEndDate(v);
                      }}
                      ariaLabel="Last day"
                      data-testid="schedule-edit-end-date"
                    />
                  )}
                </div>
              )}
            </>
          )}
        </section>

        <section className="nx-sched-edit__section" aria-labelledby="nx-sched-edit-name">
          <h3 className="nx-sched-edit__h" id="nx-sched-edit-name">
            Name
          </h3>
          <label className="nx-task-form__row">
            <span>Schedule name</span>
            <input
              type="text"
              value={name}
              maxLength={NAME_MAX}
              onChange={(e) => setName(e.target.value)}
              data-testid="schedule-edit-name"
            />
          </label>
        </section>

        {showApplyFrom && (
          <section
            className="nx-sched-edit__section"
            aria-labelledby="nx-sched-edit-from"
            data-testid="schedule-edit-apply-from-section"
          >
            <h3 className="nx-sched-edit__h" id="nx-sched-edit-from">
              Applies from
            </h3>
            <DateField
              className="nx-task-form__row"
              value={applyFromKey}
              min={todayKey}
              onChange={(v) => {
                setApplyFromTouched(true);
                setApplyFromKey(v && v >= todayKey ? v : todayKey);
              }}
              ariaLabel="Applies from"
              data-testid="schedule-edit-apply-from"
            />
            {/* Today means "from now", and the server keeps a run still
                to come today when the new time will not run today -- so
                "before this day" would be wrong about it. */}
            <span className="nx-task-form__hint" data-testid="schedule-edit-apply-from-hint">
              {applyFromKey <= todayKey
                ? 'From now. A run still to come today keeps its old time when the new time will not run today (it has passed, or the new pattern skips today), listed as another schedule with the same name until tonight.'
                : 'Runs before this day keep their old time, listed as another schedule with the same name until then.'}{' '}
              Runs that already happened are never changed, and a schedule never runs twice in one
              day because of an edit.
            </span>
          </section>
        )}

        {/* The summary rides in the sticky footer, right above Save: the
            answer to "what will this do" is the last thing read before
            the click, on a laptop's 720 px as much as on a tall screen. */}
        <div className="nx-task-form__footer nx-sched-edit__footer">
          {changedUnder && (
            <p className="nx-sched-edit__line is-warn" data-testid="schedule-edit-changed-under">
              This schedule was changed after you opened it. Saving puts back what this form shows
              for the parts you changed.{' '}
              {props.onReload && (
                <button
                  type="button"
                  className="nx-sched-edit__reload"
                  onClick={props.onReload}
                  data-testid="schedule-edit-reload"
                >
                  Start again from the saved schedule
                </button>
              )}
            </p>
          )}
          <div
            className="nx-sched-edit__summary"
            role="status"
            aria-live="polite"
            data-testid="schedule-edit-summary"
          >
            {problems.length > 0 ? (
              problems.map((p) => (
                <p key={p} className="nx-sched-edit__line is-warn">
                  {p}
                </p>
              ))
            ) : patch === null ? (
              <p className="nx-sched-edit__line">Nothing changed yet.</p>
            ) : previewError ? (
              <p
                className="nx-sched-edit__line is-fail"
                role="alert"
                data-testid="schedule-edit-preview-error"
              >
                Can&apos;t save this change: {errorWords(previewError)}
              </p>
            ) : lines.length === 0 ? (
              <p className="nx-sched-edit__line" data-testid="schedule-edit-checking">
                Checking the change…
              </p>
            ) : (
              lines.map((l) => (
                <p
                  key={l.id}
                  className={`nx-sched-edit__line${l.tone === 'warn' ? ' is-warn' : ''}`}
                  data-testid={`schedule-edit-line-${l.id}`}
                >
                  {l.text}
                </p>
              ))
            )}
            {blocked.length > 0 && (
              <p className="nx-sched-edit__line is-warn" data-testid="schedule-edit-maintenance">
                {blocked.length === 1
                  ? `The ${runText(blocked[0]!.ms, nowMs, tz)} run is inside blocked time${blocked[0]!.w!.reason ? ` (${blocked[0]!.w!.reason})` : ''}, so it will be skipped.`
                  : `${blocked.length} of the next runs are inside blocked time and will be skipped, the first ${runText(blocked[0]!.ms, nowMs, tz)}.`}
              </p>
            )}
          </div>
          <div className="nx-task-form__footer-row">
            <button
              type="submit"
              className="nx-task-form__submit"
              disabled={!canSave}
              title={actionsOff ? PREVIEW_LINE : undefined}
              data-testid="schedule-edit-save"
            >
              {busy ? 'Saving…' : checking ? 'Checking…' : 'Save'}
            </button>
            <button
              type="button"
              className="nx-task-form__save-favorite-toggle"
              onClick={props.onCancel}
              data-testid="schedule-edit-cancel"
            >
              Cancel
            </button>
          </div>
          {update.isError && (
            <span className="nx-task-form__error" role="alert" data-testid="schedule-edit-error">
              Not saved: {errorWords(update.error)}
            </span>
          )}
        </div>
      </fieldset>
    </form>
  );
}

/** The robot pin by its name, as the schedule panel words it
 *  (`scheduleRobotText`): "Phantas-2", or "Any cleaning robot" for one
 *  RMF picks -- a fleet pin is kept, and not named. */
const noRobotLabel = () => null;

/** The task's own fields: the create form's, for the category the
 *  schedule already has, with its type locked. */
function TaskFields(props: {
  form: TaskRequestForm;
  original: TaskRequestForm;
  onMode: (mode: CleanMode) => void;
  onValue: (key: keyof CategoryFormValues, value: string) => void;
  catalog: readonly TaskCatalogEntry[];
  places: readonly PlaceRegistry[];
  pinnedFleet: string | null;
  waitBad: boolean;
}) {
  const { form, original, catalog } = props;
  const v = form.values;
  const entry = catalog.find((e) => e.category === toV1Category(form.category));
  const placeNames = props.places.map((p) => p.name);

  if (form.category === 'clean') {
    const offered = offeredCleanModes(entry);
    const modes = offered.includes(original.cleanMode) ? offered : [original.cleanMode, ...offered];
    const zones = entry?.zones ?? [];
    const zoneGone = Boolean(v.zone) && zones.length > 0 && !zones.includes(v.zone!);
    const fleets = props.places.find((p) => p.name === v.zone)?.fleets;
    const fleetMisses =
      props.pinnedFleet !== null &&
      v.zone !== original.values.zone &&
      fleets !== undefined &&
      fleets.length > 0 &&
      !fleets.includes(props.pinnedFleet);
    return (
      <>
        <label className="nx-task-form__row">
          <span>Clean mode</span>
          <select
            value={form.cleanMode}
            onChange={(e) => props.onMode(e.target.value as CleanMode)}
            data-testid="schedule-edit-mode"
          >
            {modes.map((m) => (
              <option key={m} value={m}>
                {CLEAN_MODE_LABEL[m]}
                {offered.includes(m) ? '' : ' (not offered here now)'}
              </option>
            ))}
          </select>
        </label>
        <label className="nx-task-form__row">
          <span>Zone / area</span>
          <select
            value={v.zone ?? ''}
            onChange={(e) => props.onValue('zone', e.target.value)}
            data-testid="schedule-edit-zone"
          >
            {/* The stored zone stays choosable, and says so, when the
                site no longer offers it: the editor shows what IS, and
                the operator decides. */}
            {original.values.zone && !zones.includes(original.values.zone) && (
              <option value={original.values.zone}>
                {original.values.zone} (not offered here now)
              </option>
            )}
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
          {zoneGone && (
            <span
              className="nx-task-form__hint nx-task-form__hint--warn"
              data-testid="schedule-edit-zone-gone"
            >
              This site no longer offers {v.zone} as a cleaning zone. A run sent there may clean
              nothing.
            </span>
          )}
          {fleetMisses && (
            <span
              className="nx-task-form__hint nx-task-form__hint--warn"
              data-testid="schedule-edit-fleet-miss"
            >
              {props.pinnedFleet} does not serve {v.zone}, so no robot would take these runs.
            </span>
          )}
          {form.cleanMode === 'scrub' && (
            <span className="nx-task-form__hint">Fit and fill the water tank first.</span>
          )}
        </label>
      </>
    );
  }

  if (form.category === 'station') {
    const unknown =
      placeNames.length > 0 && Boolean(v.destination) && !placeNames.includes(v.destination!);
    return (
      <label className="nx-task-form__row">
        <span>Destination place</span>
        <input
          type="text"
          list="nx-sched-edit-places"
          value={v.destination ?? ''}
          onChange={(e) => props.onValue('destination', e.target.value)}
          data-testid="schedule-edit-destination"
        />
        <datalist id="nx-sched-edit-places">
          {placeNames.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        {unknown && (
          <span className="nx-task-form__hint nx-task-form__hint--warn">
            {v.destination} is not a place on the map.
          </span>
        )}
      </label>
    );
  }

  if (form.category === 'patrol') {
    return (
      <label className="nx-task-form__row">
        <span>Waypoints (in visit order)</span>
        <WaypointMultiSelect
          value={v.waypoints ?? ''}
          onChange={(w) => props.onValue('waypoints', w)}
          options={placeNames}
          testId="schedule-edit-waypoints"
        />
      </label>
    );
  }

  // dustbag and charge: one place from the catalog's short declared list.
  const listed = entry?.places ?? [];
  const key = form.category === 'dustbag' ? 'dustbagStation' : 'chargePlace';
  const stored = original.values[key];
  return (
    <>
      <label className="nx-task-form__row">
        <span>{form.category === 'dustbag' ? 'Maintenance point' : 'Charger place'}</span>
        <select
          value={v[key] ?? ''}
          onChange={(e) => props.onValue(key, e.target.value)}
          data-testid="schedule-edit-place"
        >
          {stored && !listed.includes(stored) && (
            <option value={stored}>{stored} (not offered here now)</option>
          )}
          {listed.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>
      {form.category === 'dustbag' && (
        <label className="nx-task-form__row">
          <span>Wait for staff up to (minutes)</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={v.dustbagWaitMinutes ?? ''}
            onChange={(e) => props.onValue('dustbagWaitMinutes', e.target.value)}
            aria-invalid={props.waitBad ? true : undefined}
            data-testid="schedule-edit-wait"
          />
          <span className={`nx-task-form__hint${props.waitBad ? ' nx-task-form__hint--warn' : ''}`}>
            Whole minutes from {DUSTBAG_WAIT_MIN_MINUTES} to {DUSTBAG_WAIT_MAX_MINUTES}. Leave empty
            for the site&apos;s usual wait.
          </span>
        </label>
      )}
    </>
  );
}
